<template>
  <nav class="menu" data-qa="menu">
    <ul :class="['menu__list', { reversed: isShownAdditionalTab }]" ref="navWrapper">
      <li
        v-for="r in routes"
        :key="r.label"
        :class="['menu__item', { menu__item_aside: !r.route }]"
        :data-qa="r.label"
      >
        <router-link
          v-if="r.route && !r.external"
          :class="['menu__link', { 'router-link-active': isActiveTab(r) }]"
          :to="r.route"
          @click="trackEvent(r.label)"
          >{{ r.label }}</router-link
        >
        <a v-else-if="r.route && r.external" @click="trackEvent(r.label)" class="menu__link" :href="r.route as string">{{
          r.label
        }}</a>
        <div
          v-else
          :class="['menu__aside', 'aside-menu', { 'aside-menu__active': isActiveTab(r) }]"
        >
          <button
            class="aside-menu__label aside-menu__label_dropdown"
            :aria-label="r.label"
            :aria-controls="`submenu-${r.label}`"
            aria-expanded="false"
            @keyup.enter="keyboardOpenMenuHandler"
          >
            {{ r.label }}
          </button>

          <ul class="aside-menu__list" :id="`submenu-${r.label}`">
            <li v-for="(cr, count) in r.childRoutes" :key="cr.label" class="aside-menu__item">
              <router-link
                v-if="!cr.external"
                class="aside-menu__link"
                @click="trackEvent(cr.label)"
                :to="cr.route as RouteLocationRaw"
                @keyup.tab="
                  (e) => {
                    keyboardCloseMenuHandler(e, count, r.childRoutes?.length as number)
                  }
                "
                >{{ cr.label }}</router-link
              >
              <a
                v-else
                @click="trackEvent(cr.label)"
                class="aside-menu__link"
                @keyup.tab="
                  (e) => {
                    keyboardCloseMenuHandler(e, count, r.childRoutes?.length as number)
                  }
                "
                :href="cr.route as string"
                target="_blank"
                rel="noopener"
                >{{ cr.label }}</a
              >
            </li>
          </ul>
        </div>
      </li>
      <li v-if="isShownAdditionalTab" class="menu__item"></li>
    </ul>
  </nav>
</template>

<script setup lang="ts">
  import { useGtm } from '@gtm-support/vue-gtm';
  import { computed } from 'vue';
  import { RouteLocationRaw, useRoute } from 'vue-router';

  import { NavigationLink } from '@/types/interfaces';

  const trackEvent = (linkText: string) => {
      const gtm = useGtm();
      gtm?.trackEvent({
        event: 'simple_interaction',
        elementType: 'link',
        elementName: 'Mobile Header Navigation',
        elementText: linkText,
      });
    };

  const props = defineProps<{
    routes: NavigationLink[]
  }>();

  const route = useRoute();

  const isShownAdditionalTab = computed(() => props.routes.length === 1);

  const isActiveTab = (routeTab: NavigationLink): boolean => {
    if (routeTab.additionalRoute) {
      return routeTab.additionalRoute.name === route.matched[0].name;
    }
    if (routeTab.childRoutes) {
      return !!routeTab.childRoutes.find((childRoute) => childRoute.name === route.name);
    }
    return false;
  };

  const keyboardOpenMenuHandler = (event: KeyboardEvent): void => {
    const target = event.target as HTMLElement;
    const ulMenuEl = target.nextElementSibling as HTMLElement;

    if (target.getAttribute('aria-expanded') == 'true') {
      target.setAttribute('aria-expanded', 'false');
      ulMenuEl.classList.remove('active');
    } else {
      target.setAttribute('aria-expanded', 'true');
      ulMenuEl.classList.add('active');
    }
  };

  const keyboardCloseMenuHandler = (
    event: KeyboardEvent,
    count: number,
    countLength: number,
  ): void => {
    if (count == countLength - 1) {
      const target = event.target as HTMLElement;
      const ulMenuEl = target?.parentElement?.parentElement as HTMLElement;
      const useActionsMenu = document?.querySelector(
        '.header__user-actions_desktop .user-actions__toggle-dropdown',
      ) as HTMLElement;
      const menuButton = ulMenuEl?.previousElementSibling as HTMLElement;

      menuButton.setAttribute('aria-expanded', 'false');
      ulMenuEl.classList.remove('active');

      const nextMenuItemToFocus =
        ulMenuEl.parentElement?.parentElement?.nextElementSibling?.querySelector('a');

      if (nextMenuItemToFocus) nextMenuItemToFocus.focus();
      else {
        useActionsMenu.focus();
      }
    }
  };
</script>

<style scoped lang="scss">
  .menu {
    width: 100%;
    overflow-x: scroll;
    scrollbar-width: none;

    &__list {
      display: flex;
      justify-content: flex-start;
      width: 100%;

      &.reversed {
        flex-direction: row-reverse;
      }
    }

    &__item {
      list-style-type: none;
      display: block;

      white-space: nowrap;

      &:first-child {
        margin-left: 0;
      }
    }

    &__link {
      display: block;
      justify-content: center;
      text-align: center;
      min-width: 90px;
      padding: 0 12px;
      font-size: remc(15px);
      font-weight: 500;
      line-height: remc($tabs-header-height);
      color: $link-text;
      text-decoration: none;
      background: $white-rgb-20;
      border-radius: 2px 2px 0 0;

      &:hover {
        background: $light-navy-5;
      }

      &.router-link-active {
        color: $red-9;
        padding: 0 4px;
        border-bottom: 4px solid $red-9;
      }

      line-height: remc(35px);
      background: $white;
    }

    &__aside {
      background: $white;
    }

    width: 100%;

    &__item {
      width: 100%;
    }
  }

  .aside-menu {
    $p: &;
    display: block;
    justify-content: center;
    min-width: 100px;
    color: $link-text;
    text-decoration: none;
    background: $white-rgb-20;
    border-radius: 2px 2px 0 0;

    &:hover {
      background: $light-navy-5;

      #{$p}__list {
        z-index: 12;
        display: block;
      }
      #{$p}__label_dropdown:after {
        rotate: (0deg);
        transform: translate(0, 50%);
      }
    }

    &__label {
      padding: 0 12px;
      font-size: remc(15px);
      font-weight: 500;
      line-height: remc($tabs-header-height);
      color: $link-text;
      background: transparent;
      border-bottom: 4px solid transparent;

      &_dropdown {
        padding: 0 30px 0 12px;
        position: relative;

        &:after {
          position: absolute;
          right: 5px;
          width: 16px;
          height: 16px;
          content: '';
          background-color: transparent;
          background-image: url('@/assets/icons/chevron-blue-dropdown.svg');
          background-repeat: no-repeat;
          background-position: center;
          rotate: (180deg);
          transform: translate(0, -50%);
        }
      }
    }

    &__active {
      color: $red-9;
      border-bottom: 4px solid $red-9;

      .aside-menu__label_dropdown {
        color: $red-9;
        &:after {
          position: absolute;
          background-image: url('@/assets/icons/chevron-red-dropdown.svg');
          fill: $red-9;
        }
      }
    }

    &__list {
      position: absolute;
      margin-top: 2px;
      left: 0;
      z-index: 1;
      display: none;
      width: 100%;
      background: $white;
      border-top: none;
      border-radius: 0 0 4px 4px;

      &.router-link-active {
        position: relative;
        background: transparent;
      }

      @include new-sm {
        width: 50%;
      }
    }

    &__item {
      border-bottom: 1px solid $light-navy-40;

      &:last-child {
        border-bottom: none;
      }
    }

    &__link {
      display: block;
      padding: 14px 16px;
      font-size: remc(14px);
      font-weight: 500;
      line-height: remc(20px);
      background: #f6f6f8;
      color: $link-text;
    }
  }
</style>
